/**
 * Lead Magnet Modal Component
 *
 * Modal to offer free downloadable content in exchange for email.
 * Can be triggered from various CTAs across the landing page.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { supabase } from '../../services/supabaseService';
import { trackLeadMagnetDownload, getStoredUTMParams } from '../../utils/analytics';

export interface LeadMagnet {
  id: string;
  title: string;
  description: string;
  benefits: string[];
  downloadUrl: string;
  imageUrl?: string;
}

// Available lead magnets
export const LEAD_MAGNETS: Record<string, LeadMagnet> = {
  lgpd_checklist: {
    id: 'lgpd_checklist',
    title: 'Checklist LGPD para Clínicas de Psicologia',
    description: 'Guia completo com 25 itens essenciais para adequar sua clínica à Lei Geral de Proteção de Dados.',
    benefits: [
      'Checklist prático de 25 itens',
      'Modelos de termos de consentimento',
      'Guia de boas práticas para prontuários',
      'Atualizado para 2025',
    ],
    downloadUrl: '/downloads/checklist-lgpd-clinicas.pdf',
  },
  reduce_no_shows: {
    id: 'reduce_no_shows',
    title: 'Como Reduzir Faltas em 50%',
    description: 'Estratégias comprovadas para diminuir as faltas e cancelamentos de última hora.',
    benefits: [
      '7 estratégias testadas',
      'Scripts de mensagens prontos',
      'Modelo de política de cancelamento',
      'Checklist de implementação',
    ],
    downloadUrl: '/downloads/guia-reduzir-faltas.pdf',
  },
  clinic_management: {
    id: 'clinic_management',
    title: '10 Dicas para Gestão de Consultório',
    description: 'As melhores práticas para organizar e fazer crescer seu consultório.',
    benefits: [
      '10 dicas práticas',
      'Planilha de controle financeiro',
      'Modelo de agenda otimizada',
      'Guia de precificação',
    ],
    downloadUrl: '/downloads/guia-gestao-consultorio.pdf',
  },
};

interface LeadMagnetModalProps {
  open: boolean;
  onClose: () => void;
  magnetId?: string;
}

const LeadMagnetModal: React.FC<LeadMagnetModalProps> = ({
  open,
  onClose,
  magnetId = 'lgpd_checklist',
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const magnet = LEAD_MAGNETS[magnetId] || LEAD_MAGNETS.lgpd_checklist;

  // Colors
  const primaryColor = '#1B6A63';
  const primaryLight = '#27AB83';
  const bgColor = isDarkMode ? '#1a1a2e' : '#ffffff';
  const contentBgColor = isDarkMode ? '#16213e' : '#f8faf9';
  const textColor = isDarkMode ? '#e8e8e8' : '#1a1a1a';
  const textSecondary = isDarkMode ? '#a0a0a0' : '#666666';

  const handleClose = () => {
    setEmail('');
    setName('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError('Por favor, insira um email válido');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const utmParams = getStoredUTMParams();

      const { error: insertError } = await supabase
        .from('leads')
        .insert({
          email: email.toLowerCase().trim(),
          name: name.trim() || null,
          source: 'lead_magnet',
          status: 'new',
          message: `Download: ${magnet.title}`,
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          referrer_url: utmParams.referrer || null,
        });

      if (insertError && insertError.code !== '23505') {
        throw insertError;
      }

      setSuccess(true);
      trackLeadMagnetDownload(magnet.id, true);

      setTimeout(() => {
        window.open(magnet.downloadUrl, '_blank');
      }, 1500);
    } catch (err) {
      console.error('Error saving lead magnet lead:', err);
      setError('Erro ao processar. Tente novamente.');
      trackLeadMagnetDownload(magnet.id, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          m: 2,
          bgcolor: bgColor,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          color: 'white',
          bgcolor: alpha('#000', 0.2),
          backdropFilter: 'blur(4px)',
          '&:hover': {
            bgcolor: alpha('#000', 0.4),
          },
          width: 36,
          height: 36,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Header with gradient */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`,
            color: 'white',
            py: 5,
            px: 4,
            textAlign: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -20,
              left: 0,
              right: 0,
              height: 40,
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`,
              clipPath: 'ellipse(60% 100% at 50% 0%)',
            },
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 3,
              bgcolor: alpha('#fff', 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
              backdropFilter: 'blur(8px)',
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography
            variant="h5"
            fontWeight="700"
            gutterBottom
            sx={{
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              lineHeight: 1.3,
            }}
          >
            {magnet.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {magnet.description}
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            p: 4,
            pt: 5,
            bgcolor: contentBgColor,
          }}
        >
          {success ? (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(primaryLight, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: primaryLight }} />
              </Box>
              <Typography
                variant="h5"
                sx={{ color: primaryColor, fontWeight: 700, mb: 1.5 }}
              >
                Download iniciando...
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: textSecondary, mb: 3, lineHeight: 1.6 }}
              >
                Verifique sua pasta de downloads.<br />
                Também enviamos uma cópia para seu email.
              </Typography>
              <Button
                variant="outlined"
                href={magnet.downloadUrl}
                target="_blank"
                startIcon={<DownloadIcon />}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  borderRadius: 2,
                  px: 3,
                  '&:hover': {
                    borderColor: primaryLight,
                    bgcolor: alpha(primaryColor, 0.05),
                  },
                }}
              >
                Baixar novamente
              </Button>
            </Box>
          ) : (
            <>
              {/* Benefits list */}
              <Typography
                variant="subtitle2"
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.75rem',
                }}
              >
                O que você vai receber:
              </Typography>

              <Box sx={{ mb: 3 }}>
                {magnet.benefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        fontSize: 20,
                        color: primaryLight,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: textColor,
                        lineHeight: 1.5,
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Como podemos te chamar?"
                  disabled={isSubmitting}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: bgColor,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  type="email"
                  label="Seu email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemplo@email.com"
                  required
                  disabled={isSubmitting}
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: bgColor,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2.5,
                      borderRadius: 2,
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  sx={{
                    py: 1.75,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`,
                    boxShadow: `0 4px 14px ${alpha(primaryColor, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, #155C56 0%, #1F9972 100%)`,
                      boxShadow: `0 6px 20px ${alpha(primaryColor, 0.5)}`,
                    },
                    '&:disabled': {
                      background: isDarkMode ? '#333' : '#ccc',
                    },
                  }}
                >
                  {isSubmitting ? 'Processando...' : 'Baixar Grátis'}
                </Button>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  mt: 2.5,
                  display: 'block',
                  textAlign: 'center',
                  color: textSecondary,
                }}
              >
                100% grátis. Não enviamos spam.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LeadMagnetModal;
